/**
 * Where a person lands when their e-filing session ends.
 *
 * Sign-in for both products happens at the Tab Service portal, so signing out of e-filing
 * has to end the portal session as well: the browser is sent to the portal's own sign-out
 * page, which clears that session and lands on the login page. When no portal is configured
 * (local development, mock mode) the app keeps its own sign-in page, as before.
 *
 * The other direction is the `/sign-out` route in this app: the portal sends the browser
 * there with a `return` address, which is honoured only when it points back at the portal.
 */
import { env } from './env';

/** The portal's sign-out page. It ends the portal session and lands on the login page. */
export const PORTAL_SIGN_OUT_PATH = '/logout';

/** The origin of the configured portal, or null when there is none. */
export function portalOrigin(): string | null {
  if (!env.portalWebUrl) return null;
  try {
    return new URL(env.portalWebUrl).origin;
  } catch {
    return null;
  }
}

/**
 * Where to send the browser after signing out, or null to stay in this app.
 *
 * Mock mode never leaves: there is no portal behind a demo session.
 */
export function signOutDestination(): string | null {
  if (env.mock) return null;
  const origin = portalOrigin();
  return origin ? `${origin}${PORTAL_SIGN_OUT_PATH}` : null;
}

/**
 * The address the portal asked us to return to, kept only when it is the portal itself.
 * Anything else — another host, a protocol-relative address, a javascript: URL — is dropped
 * and the caller falls back to the portal's login page.
 */
export function safePortalReturn(value: string | null | undefined): string | null {
  const origin = portalOrigin();
  if (!value || !origin) return null;
  let url: URL;
  try {
    url = new URL(value, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  return url.toString();
}

/**
 * Where to send somebody who asked to sign in here, or null to show this app's own form.
 *
 * The portal is the single front door: one sign-in covers both products, so there is no
 * second place to sign in and this app offers none. Anybody who reaches e-filing without a
 * session is sent to the portal's login page.
 *
 * The form survives in exactly two cases, both of them local: mock mode, where there is no
 * portal behind a demo session, and a build with no portal configured at all. Neither is
 * deployed, and in both the alternative would be an app nobody can enter.
 */
export function signInElsewhere(): string | null {
  if (env.mock) return null;
  return portalLoginUrl();
}

/**
 * The portal's own front page, for somebody who has an account there as well. The site
 * picker rather than a dashboard: which site they want is the portal's question to ask.
 */
export function portalHomeUrl(): string | null {
  const origin = portalOrigin();
  return origin ? `${origin}/sites` : null;
}

/** The portal's login page: where a `/sign-out` visit lands when no return address is usable. */
export function portalLoginUrl(): string | null {
  const origin = portalOrigin();
  return origin ? `${origin}/login` : null;
}

/**
 * Leave, now that the session on this domain is gone.
 *
 * With a portal configured the browser goes there to have that session ended too, which is
 * what "signed out" has to mean once one sign-in covers both products. Without one, the
 * caller keeps the person here, on this app's own sign-in page.
 */
export function leaveAfterSignOut(stayHere: () => void): void {
  const destination = signOutDestination();
  if (destination) {
    window.location.assign(destination);
    return;
  }
  stayHere();
}
